import React, { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import AdminSidebar from "../../components/admin/AdminSidebar";
import Modal from "../../components/shared/Modal";
import { SkeletonCard } from "../../components/shared/Skeleton";
import SmartDataTable from "../../components/shared/SmartDataTable";
import NumberBalls from "../../components/shared/NumberBalls";
import {
  Bell, CheckCircle2, FileClock, FileText, Hash, ImagePlus,
  Loader2, Search, Ticket, Upload,
} from "lucide-react";
import { ticketRequestsApi, ticketsApi } from "../../services/api";

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-";
}

function fmt(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
}

function requestTone(status = "") {
  if (["paid", "assigned"].includes(status)) return "live";
  if (["rejected", "cancelled"].includes(status)) return "danger";
  return "warn";
}

function listFromResponse(response) {
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
}

const LIMIT = 25;
const EMPTY = {
  ticketNumber: "",
  request: null,
  drawDate: null,
  whiteBalls: ["", "", "", "", ""],
  powerball: "",
  image: null,
};

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef();

  async function load(p = 1) {
    setLoading(true);
    setError("");
    const requestResult = await ticketRequestsApi.getAll({ limit: 100 })
      .then((res) => ({ ok: true, res }))
      .catch((err) => ({ ok: false, err }));

    if (requestResult.ok) {
      setRequests(listFromResponse(requestResult.res));
    } else {
      setRequests([]);
    }

    const ticketResult = await ticketsApi.getAll({ limit: 1000, page: p })
      .then((res) => ({ ok: true, res }))
      .catch((err) => ({ ok: false, err }));

    if (ticketResult.ok) {
      const ticketRes = ticketResult.res;
      setTickets(listFromResponse(ticketRes));
      setTotal(ticketRes.data.total || 0);
    } else {
      setTickets([]);
      setTotal(0);
      if (!requestResult.ok) setError(ticketResult.err?.response?.data?.message || "Unable to load ticket operations.");
    }

    setLoading(false);
  }

  useEffect(() => { load(page); }, [page]);

  const requestOptions = useMemo(() => requests
    .filter((r) => ["paid", "partially_assigned"].includes(r.status))
    .map((r) => ({
      value: r._id,
      label: `${r.requestId || r._id?.slice(-6)} - ${r.user?.name || "User"} - ${r.pool?.poolId || "Pool"} (${r.assignedTickets?.length || 0}/${r.quantity})`,
      data: r,
    })), [requests]);

  const selectedRequest = form.request?.data;

  const requestColumns = [
    { name: "Request", selector: (row) => row.requestId || row._id || "", sortable: true, cell: (row) => <strong>{row.requestId || row._id?.slice(-6)}</strong> },
    { name: "User", selector: (row) => row.user?.name || "", sortable: true, cell: (row) => <span>{row.user?.name || "User"}<small className="table-muted">{row.user?.email || ""}</small></span>, grow: 1.35 },
    { name: "Pool", selector: (row) => row.pool?.poolId || "", sortable: true, cell: (row) => <span>{row.pool?.poolId || "-"}<small className="table-muted">{row.pool?.name || ""}</small></span>, grow: 1.25 },
    { name: "Qty", selector: (row) => row.quantity || 0, sortable: true },
    { name: "Paid", selector: (row) => row.totalAmount || 0, sortable: true, cell: (row) => <b className="table-primary">{fmt(row.totalAmount)}</b> },
    { name: "Assigned", selector: (row) => row.assignedTickets?.length || 0, sortable: true, cell: (row) => `${row.assignedTickets?.length || 0} / ${row.quantity}` },
    { name: "Status", selector: (row) => row.status || "", sortable: true, cell: (row) => <span className={`table-status ${requestTone(row.status)}`}>{row.status?.replaceAll("_", " ")}</span> },
    {
      name: "Action",
      button: true,
      cell: (row) => ["paid", "partially_assigned"].includes(row.status) ? (
        <button onClick={() => openForRequest(row)} title="Assign purchased ticket"><Upload size={14} /></button>
      ) : null,
    },
  ];

  const ticketColumns = [
    { name: "Ticket", selector: (row) => row.ticketNumber || "", sortable: true, cell: (row) => <span className="table-code">{row.ticketNumber}</span> },
    { name: "Pool", selector: (row) => row.pool?.poolId || "", sortable: true, cell: (row) => row.pool?.poolId || "-" },
    { name: "Owner", selector: (row) => row.owner?.name || "", sortable: true, cell: (row) => row.owner?.name || row.ticketRequest?.user?.name || "Assigned user", grow: 1.2 },
    { name: "Draw", selector: (row) => row.drawDate || "", sortable: true, cell: (row) => fmtDate(row.drawDate) },
    { name: "Numbers", cell: (row) => <NumberBalls numbers={row.whiteBalls} power={row.powerball} />, grow: 1.6 },
    { name: "Status", selector: (row) => row.status || "", sortable: true, cell: (row) => <span className={`table-status ${row.status === "winner" ? "live" : row.status === "no_match" ? "danger" : "warn"}`}>{row.status}</span> },
  ];

  function handleBall(i, val) {
    setForm((f) => {
      const b = [...f.whiteBalls];
      b[i] = val;
      return { ...f, whiteBalls: b };
    });
    setFormError("");
  }

  function handleClose() {
    setOpen(false);
    setForm(EMPTY);
    setFormError("");
  }

  function openForRequest(request) {
    setForm({ ...EMPTY, request: requestOptions.find((o) => o.value === request._id) || null, drawDate: request.pool?.drawDate ? new Date(request.pool.drawDate) : null });
    setOpen(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selectedRequest) { setFormError("Select a paid ticket request."); return; }
    if (!form.ticketNumber.trim()) { setFormError("Ticket number is required."); return; }
    if (!form.drawDate) { setFormError("Draw date is required."); return; }
    const balls = form.whiteBalls.map(Number);
    if (balls.some((n) => !n || n < 1 || n > 69)) { setFormError("All 5 white ball numbers must be between 1 and 69."); return; }
    if (!form.powerball || Number(form.powerball) < 1 || Number(form.powerball) > 26) {
      setFormError("Powerball number must be between 1 and 26.");
      return;
    }

    const fd = new FormData();
    fd.append("ticketNumber", form.ticketNumber);
    fd.append("pool", selectedRequest.pool?._id || selectedRequest.pool);
    fd.append("owner", selectedRequest.user?._id || selectedRequest.user);
    fd.append("ticketRequest", selectedRequest._id);
    fd.append("drawDate", form.drawDate.toISOString());
    fd.append("whiteBalls", JSON.stringify(balls));
    fd.append("powerball", form.powerball);
    if (form.image) fd.append("image", form.image);

    setSaving(true);
    try {
      await ticketsApi.create(fd);
      handleClose();
      load(1);
      setPage(1);
    } catch (err) {
      setFormError(err.response?.data?.message || "Failed to assign ticket.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="admin-shell">
      <AdminSidebar />
      <main className="admin-main">
        <header className="admin-topbar">
          <div className="admin-title">Tickets</div>
          <label className="admin-search"><Search size={15} /><input placeholder="Search requests or tickets..." /></label>
          <div className="system-live"><i /> System Live</div>
          <button className="icon-button" aria-label="Notifications"><Bell size={18} /><i /></button>
        </header>

        <div className="admin-content">
          {error && <div className="auth-error" style={{ marginBottom: 16 }}>{error}</div>}
          <section className="stats-grid">
            <article className="workflow-stat"><FileClock size={20} /><span>Paid Requests</span><strong>{requests.filter((r) => r.status === "paid").length}</strong></article>
            <article className="workflow-stat"><Ticket size={20} /><span>Partial Assignments</span><strong>{requests.filter((r) => r.status === "partially_assigned").length}</strong></article>
            <article className="workflow-stat"><CheckCircle2 size={20} /><span>Completed Requests</span><strong>{requests.filter((r) => r.status === "assigned").length}</strong></article>
            <article className="workflow-stat"><FileText size={20} /><span>Uploaded Tickets</span><strong>{total}</strong></article>
          </section>

          <section className="admin-panel request-ops-panel">
            <div className="panel-heading">
              <h2><FileClock size={18} /> Purchase Requests <span className="muted-count">{requests.length} total</span></h2>
              <button onClick={() => setOpen(true)}><Upload size={14} /> Assign Ticket</button>
            </div>
            {loading ? (
              <div style={{ display: "grid", gap: 1 }}>
                {Array.from({ length: 4 }).map((_, i) => <div key={i} style={{ borderBottom: "1px solid var(--line)", padding: "20px 24px" }}><SkeletonCard lines={2} /></div>)}
              </div>
            ) : requests.length === 0 ? (
              <div className="empty-state" style={{ margin: 24 }}><FileClock size={32} /><p>No ticket purchase requests yet.</p></div>
            ) : (
              <SmartDataTable
                columns={requestColumns}
                data={requests}
                loading={false}
                searchFields={["requestId", "user.name", "user.email", "pool.poolId", "pool.name", "status"]}
                searchPlaceholder="Search requests by user, pool, status..."
                emptyMessage="No ticket purchase requests yet."
                paginationPerPage={25}
              />
            )}
          </section>

          <section className="admin-panel" style={{ padding: 0 }}>
            <div className="panel-heading">
              <h2><FileText size={18} /> Assigned Tickets <span className="muted-count">{total} total</span></h2>
            </div>
            {loading ? (
              <div style={{ display: "grid", gap: 1 }}>
                {Array.from({ length: 5 }).map((_, i) => <div key={i} style={{ borderBottom: "1px solid var(--line)", padding: "20px 24px" }}><SkeletonCard lines={2} /></div>)}
              </div>
            ) : tickets.length === 0 ? (
              <div className="empty-state" style={{ margin: 24 }}><FileText size={32} /><p>No tickets assigned yet.</p></div>
            ) : (
              <SmartDataTable
                columns={ticketColumns}
                data={tickets}
                loading={false}
                searchFields={["ticketNumber", "pool.poolId", "pool.name", "owner.name", "owner.email", "status"]}
                searchPlaceholder="Search assigned tickets..."
                emptyMessage="No tickets assigned yet."
                paginationPerPage={25}
              />
            )}
          </section>
        </div>
      </main>

      <Modal open={open} onClose={handleClose} title="Assign Purchased Ticket" subtitle="Attach the manually purchased ticket to a paid user request" icon={<Upload size={18} />} size="md">
        <form className="modal-form" onSubmit={handleSubmit}>
          {formError && <div className="auth-error">{formError}</div>}

          <div className="auth-field">
            <label>Paid Request</label>
            <Select
              classNamePrefix="lp-select"
              options={requestOptions}
              value={form.request}
              onChange={(v) => {
                setForm((f) => ({ ...f, request: v, drawDate: v?.data?.pool?.drawDate ? new Date(v.data.pool.drawDate) : f.drawDate }));
                setFormError("");
              }}
              placeholder="Select paid request..."
              isClearable
            />
          </div>

          {selectedRequest && (
            <div className="request-confirm-card compact">
              <span>{selectedRequest.user?.name || "User"} - {selectedRequest.pool?.poolId || "Pool"}</span>
              <strong>{selectedRequest.assignedTickets?.length || 0} / {selectedRequest.quantity} assigned</strong>
              <small>{fmt(selectedRequest.totalAmount)} paid for this request</small>
            </div>
          )}

          <div className="modal-row">
            <div className="auth-field">
              <label><Hash size={13} style={{ marginRight: 6 }} />Ticket Number</label>
              <input value={form.ticketNumber} onChange={(e) => { setForm((f) => ({ ...f, ticketNumber: e.target.value })); setFormError(""); }} placeholder="e.g. PB-44018" />
            </div>
            <div className="auth-field">
              <label>Powerball Draw Date</label>
              <DatePicker selected={form.drawDate} onChange={(d) => { setForm((f) => ({ ...f, drawDate: d })); setFormError(""); }} showTimeSelect timeFormat="HH:mm" timeIntervals={15} dateFormat="MMM d, yyyy h:mm aa" placeholderText="Select draw date..." popperPlacement="bottom-start" />
            </div>
          </div>

          <div>
            <span className="ball-label">White Ball Numbers <span style={{ color: "rgba(210,197,175,.45)", fontWeight: 400 }}>(1 - 69)</span></span>
            <div className="ball-inputs">
              {form.whiteBalls.map((v, i) => (
                <input key={i} type="number" min={1} max={69} value={v} onChange={(e) => handleBall(i, e.target.value)} className="ball-input" placeholder={i + 1} />
              ))}
            </div>
          </div>

          <div style={{ maxWidth: 150 }}>
            <span className="ball-label" style={{ color: "rgba(244,182,192,.8)" }}>Powerball <span style={{ color: "rgba(210,197,175,.45)", fontWeight: 400 }}>(1 - 26)</span></span>
            <input type="number" min={1} max={26} value={form.powerball} onChange={(e) => { setForm((f) => ({ ...f, powerball: e.target.value })); setFormError(""); }} className="ball-input power" placeholder="PB" />
          </div>

          <div>
            <span className="ball-label">Ticket Image Proof <span style={{ color: "rgba(210,197,175,.45)", fontWeight: 400 }}>(optional)</span></span>
            <div className={`file-upload-zone ${form.image ? "has-file" : ""}`} onClick={() => fileRef.current?.click()}>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setForm((f) => ({ ...f, image: e.target.files[0] }))} />
              <ImagePlus size={28} />
              {form.image ? <><strong>{form.image.name}</strong><span>{(form.image.size / 1024).toFixed(0)} KB</span></> : <><span>Click to upload ticket image</span><span style={{ fontSize: 11 }}>JPEG, PNG or WebP - max 5 MB</span></>}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="modal-btn-cancel" onClick={handleClose}>Cancel</button>
            <button type="submit" className="modal-btn-submit" disabled={saving}>
              {saving ? <><Loader2 size={14} className="spin" /> Assigning...</> : <><Upload size={14} /> Assign Ticket</>}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
